// ───────────────────────────────────────────────────────────────────────────
// Operator Manual — "Walk Me Through It Like I'm Five"
// Built for Shannon (CEO) and Shaneka (COO) so they can master the dashboard
// without anyone over their shoulder. Spoken-word friendly, no tech jargon.
//
// Trigger: <button data-testid="open-operator-manual"> on HomeTab.js, or
// dispatch `window.dispatchEvent(new Event("manual:open"))` from anywhere.
// ───────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HUBS = [
  {
    key: "today",
    icon: "🏠",
    label: "Today",
    color: "#00FFFF",
    one_liner: "What needs you right now. Open this first every morning.",
    when_use: "Every single morning before you do anything else. Coffee → open dashboard → land on Today.",
    walk_through: [
      "Look at the colored cards at the top — they tell you exactly how many Active Projects, Open Tasks, Clients, and how Launch-Ready you are.",
      "Scroll down to 'Follow-up Needed' — if any client has gone stale, just click 'Send Follow-up' and the system writes the email for you.",
      "Look at the 'Client Pipeline' board — every project sits in a column (Lead → Walk-Through → Proposal → Active → Complete). Drag your eyes left to right, that's your money moving.",
      "If a red Trigger alert pops up at the top, click it — that's your crew telling you something's wrong on a job site. Reply right there.",
    ],
    auto_runs: [
      "Follow-up emails write themselves — you just hit Send.",
      "Pipeline updates itself every time a project status changes.",
      "Field Triggers from your crew show up here automatically with a magic-link so the owner can chat too.",
    ],
  },
  {
    key: "get-clients",
    icon: "🎯",
    label: "Get Clients",
    color: "#A78BFA",
    one_liner: "This is where you go hunting. Prospects, pitches, auto-pilot.",
    when_use: "Anytime you want to bring NEW money into the business. Mondays + Wednesdays are great pitch days.",
    walk_through: [
      "Click 'Prospects' to load a list of DFW owners / asset managers you can reach out to.",
      "Click 'Pitch Center' to spin up a custom institutional pitch PDF for a specific property. Type the address, pick the unit type, hit Generate — the PDF now has Jim's Executive Briefing baked in.",
      "Click 'Auto-Pilot' to set up sequences that follow up with prospects automatically (so you never have to remember to email them again).",
      "Click 'Contracts' to pull any of your signed legal templates — MSA, Habitability Disclosure, Founders Agreement, all already attorney-leveled.",
      "Click 'Voucher Coalitions' to track Section 8 / housing authority relationships.",
    ],
    auto_runs: [
      "Pitch PDFs include the institutional Cushman briefing, palette catalog, and labor matrix math — all generated, you just download.",
      "Auto-Pilot sequences send Day 0 / Day 10 / Day 25 follow-ups without you lifting a finger.",
    ],
  },
  {
    key: "run-projects",
    icon: "🦺",
    label: "Run Projects",
    color: "#00DC78",
    one_liner: "Where the actual work happens. Crew schedules, task boards, client chats.",
    when_use: "Every time you have an active job. Open this 2-3 times a day.",
    walk_through: [
      "Click 'Projects' to see every job you've got. Click one to open it — you'll see scope, photos, contracts, status, everything.",
      "Click 'Tasks' to see your task board (To Do / Doing / Done). Drag cards to update — Shaneka lives here.",
      "Click 'Schedule' to see who's working where today.",
      "Click 'Messages' to see all client chats in one place.",
      "Click 'Clients' to see your full client roster.",
      "Click 'Field Mode' — this is the BIG GREEN BUTTON for the crew. Hand your phone to a worker, they pull up specs and snap before/after photos. Everything auto-watermarks.",
    ],
    auto_runs: [
      "Every photo the crew uploads gets stamped with address + date/time + uploader (your legal armor).",
      "If a crew member mentions a danger word (mold, bed bug, etc.), the project clock AUTO-FREEZES for 48 hours and you get a legal-notice email.",
      "Hazard photos fire a Trigger thread to you, Shaneka, AND the owner with a magic-link.",
    ],
  },
  {
    key: "design-source",
    icon: "🎨",
    label: "Design & Source",
    color: "#FFB347",
    one_liner: "Your creative engine. Before/after platform mockups, palette library, product sourcing.",
    when_use: "When pitching a new client, picking finishes, or sourcing materials at wholesale.",
    walk_through: [
      "Click '🎨 Design Studio' — upload a BEFORE photo of an empty unit, pick a palette (MFC-01 to MFC-10), pick a room type, hit Generate. The platform renders a luxury-grade AFTER mockup.",
      "Click 'Templates Library' to see your 10 signature palettes (Gallery Black, Smoky Jade, Soft Bloom, etc.) — each one has the vanity / paint / tile / hardware specs.",
      "Click 'Palette Spotlight' to feature a palette for the week (good marketing material).",
      "Click 'My Products' to see what you've sourced (Amazon, Wayfair, Sherwin-Williams pro accounts).",
      "Click 'Pro Desks' to deep-link into your wholesale vendor accounts.",
      "Click 'Scripts' for ready-to-read pitch scripts and objection handlers.",
    ],
    auto_runs: [
      "Design Studio enforces 10 Room Mandates (spa bathrooms, boutique kitchens) — so the platform can't render a bad-looking room even if you forget to ask.",
      "Every palette is NSPIRE-compliant and renter-friendly. You don't have to remember the rules.",
    ],
  },
  {
    key: "money-legal",
    icon: "🛡️",
    label: "Money & Legal",
    color: "#F472B6",
    one_liner: "Revenue, payroll, founder pay, compliance. The boring side that pays the bills.",
    when_use: "Weekly — Fridays are a great day to check this. Monthly for taxes and founder draw.",
    walk_through: [
      "Click 'Revenue' to see total income, project totals, and where money is parked.",
      "Click 'Payroll' to run crew payroll (Shaneka's lane).",
      "Click 'Founder Draw' to log money you and Shaneka take out of the company. Critical for taxes.",
      "Click 'Compliance' to see all your licenses, insurance, and TREC-firewall documents.",
      "Click 'Certifications' to track HUD / NSPIRE / BBB certifications.",
    ],
    auto_runs: [
      "Compliance dashboard turns red 30 days before any license expires.",
      "Founder draw entries auto-categorize for end-of-year tax filing.",
    ],
  },
  {
    key: "settings",
    icon: "⚙️",
    label: "Settings",
    color: "#94A3B8",
    one_liner: "Operations + Launch Readiness. The control panel.",
    when_use: "When you onboard a new crew member, change company info, or want to see how launch-ready you are.",
    walk_through: [
      "Click 'Operations' to update company settings, address, phone, email signatures.",
      "Click 'Launch' (or 'Launch Checklist') to see your 100-item readiness list. Each green check is one step closer to first paying client.",
    ],
    auto_runs: [
      "Launch readiness percentage updates in real-time as you check items off.",
    ],
  },
];

const DAILY_RHYTHM = [
  { who: "Shannon (CEO)", time: "8:00 AM", task: "Open Today hub. Read Founder Cue. Send any flagged follow-ups (1 click each)." },
  { who: "Shannon (CEO)", time: "10:00 AM", task: "Get Clients hub → Prospects → reach out to 5 new owners. Use Pitch Center if any want a proposal." },
  { who: "Shaneka (COO)", time: "9:00 AM", task: "Open Today hub. Check Tasks count. Move stale cards forward on the Task Board." },
  { who: "Shaneka (COO)", time: "11:00 AM", task: "Run Projects → Schedule. Confirm today's crew is dispatched. Pull up Field Mode if needed." },
  { who: "Both", time: "2:00 PM", task: "Quick check Today → Recent Activity. Anything weird? Click into it." },
  { who: "Both", time: "5:00 PM", task: "Money & Legal → check Revenue dashboard. Celebrate the day's wins or fix what didn't move." },
  { who: "Both", time: "Friday 4PM", task: "Money & Legal → Founder Draw → log any draws. Compliance → check anything turning red." },
];

const FAQS = [
  {
    q: "How do I talk to a Tarrant County receiver, family office, or hedge fund without sounding small?",
    a: "TWO VOICES. For small DFW landlords (Zillow prospects, mom-and-pops), the Pilot Program framing is your superpower — 'try one unit, no risk.' But for institutional money (receivers, courts, hedge funds, family offices), swap to Jim's elevated language: Do NOT say 'We are running a pilot program if you want to try us out.' INSTEAD say 'Our platform has successfully completed its closed-alpha engineering phase.' Do NOT say 'We are testing this out to see if it works.' INSTEAD say 'We just ran a 73-point targeted governance and regulatory regression suite to calibrate our Texas court-grade pipelines.' Do NOT say 'We are a small, new company.' INSTEAD say 'We are an agile, tech-forward stabilization firm operating under strict institutional guardrails.' The closed-alpha proof is what justifies your Swift +35% / Heavy +15% multipliers — they're paying for the automated risk-reduction machine, not a startup gamble.",
  },
  {
    q: "How do we defend the 50/50 payment module against corporate pushback?",
    a: "Moovin Forward LLC utilizes a strict 50% Mobilization Draw to execute immediate material procurement. In the current economic climate, immediate procurement is the only mechanism that legally guarantees and locks in the client's contract pricing against daily market inflation and supply chain volatility. We do not self-finance material floats — no queue, no dispatch, no crew on the property until materials are 100% asset-secured. If corporate counters with 10% or 20%, the answer is simple: we cannot guarantee the price or timeline without the procurement draw, full stop.",
  },
  {
    q: "How does our material pricing stay protected from inflation?",
    a: "Every estimate runs through the Material Cost Cap formula: (Base Material Estimate × 1.15) + Shipping & Fuel Surcharge. The 1.15 multiplier builds a mandatory 15% inflation/buffer cushion BEFORE the client sees a number. If the adjusted cap crosses our historic watermark, the Today tab flags it red so you and Shaneka can adjust pricing before the contract goes out. Backend endpoint: POST /api/admin/labor-matrix/material-cap.",
  },
  {
    q: "What if I see a red alert at the top of Today?",
    a: "That's a Field Trigger or a Material-Cap warning. Click it. For a Field Trigger, you'll see the crew photo, the message, and a chat where the owner is also looped in. For a Material-Cap warning, you'll see which project crossed the watermark and by what percentage so you can re-quote.",
  },
  {
    q: "Where do I send a new client their welcome email?",
    a: "Today hub → Quick Actions → 'Onboard Client'. Fill out the form. Tick 'Send welcome email'. Done. While Sandbox Mode is ON, the email goes to nivoomforward@gmail.com so you can read it first.",
  },
  {
    q: "How do I make a pitch PDF for a new owner?",
    a: "Get Clients hub → Pitch Center → type the property address → choose unit type (Studio/1BR/2BR/3BR) → hit Generate. The PDF includes Jim's Executive Briefing, the 5 palettes, and the labor matrix math automatically.",
  },
  {
    q: "What does Field Mode do?",
    a: "It's the BIG GREEN button on the Today hub. Hand your phone to a crew member. They open it on-site, snap before/after photos, scan the QR code on the unit door, and check off scope items. Every photo auto-watermarks with the address + time + their name. Court-grade.",
  },
  {
    q: "How do I deactivate a vendor or a whole trade category (kill switch)?",
    a: "Run Projects hub → Vendor Network → find the vendor → click the OPTED-IN badge → 'Deactivate'. Their token is revoked instantly. For a whole trade category (e.g., pull all plumbers), filter to the category and use the bulk Deactivate at the top. No termination liability — they were never under contract with us.",
  },
  {
    q: "Mobile dashboard acting weird? How do I hard-refresh?",
    a: "iPhone Safari: tap the AA icon in the URL bar → 'Reload Without Content Blockers'. Android Chrome: tap the 3-dot menu → tap the refresh icon while holding it (or settings → site settings → clear). Your data is safe on the server — only the local cache clears.",
  },
  {
    q: "If something breaks, who do I call?",
    a: "Open this Operator Manual back up — most answers are here. If still stuck, email yourself a note via Field Notes (it's on the Today hub). I'll see it on my next session.",
  },
];

export const OperatorManual = ({ onClose }) => {
  const [activeHub, setActiveHub] = useState("today");
  const [section, setSection] = useState("hubs"); // hubs | rhythm | faqs
  const [downloadingTruck, setDownloadingTruck] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const downloadTruckSheet = async () => {
    setDownloadingTruck(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/truck-sheet`, { credentials: "include" });
      if (!r.ok) throw new Error("Truck sheet generation failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "MF-Truck-Sheet.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Could not download Truck Sheet. Try again in a moment.");
    }
    setDownloadingTruck(false);
  };

  const current = HUBS.find((h) => h.key === activeHub) || HUBS[0];

  return (
    <div
      data-testid="operator-manual-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(2, 8, 20, 0.88)",
        backdropFilter: "blur(14px)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        animation: "fadeIn 0.18s ease",
      }}
    >
      <div
        data-testid="operator-manual-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 980,
          maxHeight: "92vh",
          background: "linear-gradient(180deg, #0A1929 0%, #050E1F 100%)",
          border: "1px solid rgba(0, 200, 200, 0.25)",
          borderRadius: 14,
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          color: "#E6EEF8",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "22px 28px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            background: "linear-gradient(135deg, rgba(0,200,200,0.06) 0%, rgba(167,139,250,0.04) 100%)",
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: "#5AC8D8", textTransform: "uppercase" }}>
              Moovin Forward · Operator Manual
            </div>
            <h2 style={{ margin: "6px 0 4px", fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "-0.01em" }}>
              Walk Me Through It — Like I'm Five
            </h2>
            <p style={{ margin: 0, color: "#94A3B8", fontSize: 13, lineHeight: 1.5 }}>
              For Shannon &amp; Shaneka. No jargon. Just clicks. Master your system one hub at a time.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={downloadTruckSheet}
              disabled={downloadingTruck}
              data-testid="manual-truck-sheet-btn"
              style={{
                background: "linear-gradient(135deg, #00DC78 0%, #5AC8D8 100%)",
                border: "none",
                color: "#030B1A",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: downloadingTruck ? "wait" : "pointer",
                fontSize: 12,
                fontWeight: 800,
                opacity: downloadingTruck ? 0.6 : 1,
              }}
            >
              {downloadingTruck ? "Building…" : "🚛 Truck Sheet PDF"}
            </button>
            <button
              onClick={onClose}
              data-testid="manual-close-btn"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                color: "#E6EEF8",
                borderRadius: 8,
                padding: "8px 14px",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Close ✕
            </button>
          </div>
        </div>

        {/* Section tabs */}
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "10px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            background: "rgba(255,255,255,0.02)",
          }}
        >
          {[
            { key: "hubs", label: "🧭 The 5 Hubs" },
            { key: "rhythm", label: "⏰ Daily Rhythm" },
            { key: "faqs", label: "❓ FAQs" },
          ].map((s) => (
            <button
              key={s.key}
              data-testid={`manual-section-${s.key}`}
              onClick={() => setSection(s.key)}
              style={{
                background: section === s.key ? "rgba(0,200,200,0.16)" : "transparent",
                border: section === s.key ? "1px solid rgba(0,200,200,0.4)" : "1px solid transparent",
                color: section === s.key ? "#00DC78" : "#94A3B8",
                padding: "7px 14px",
                borderRadius: 8,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Body — scrollable */}
        <div style={{ overflowY: "auto", flex: 1, padding: "22px 28px" }}>
          {section === "hubs" && (
            <div>
              {/* Hub picker */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 22 }}>
                {HUBS.map((h) => (
                  <button
                    key={h.key}
                    data-testid={`manual-hub-${h.key}`}
                    onClick={() => setActiveHub(h.key)}
                    style={{
                      background: activeHub === h.key ? `${h.color}22` : "rgba(255,255,255,0.04)",
                      border: activeHub === h.key ? `1.5px solid ${h.color}` : "1px solid rgba(255,255,255,0.08)",
                      color: activeHub === h.key ? h.color : "#A0B3C8",
                      padding: "9px 14px",
                      borderRadius: 9,
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.15s",
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{h.icon}</span> {h.label}
                  </button>
                ))}
              </div>

              {/* Current hub detail */}
              <div data-testid={`manual-hub-detail-${current.key}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 30 }}>{current.icon}</span>
                  <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: current.color, letterSpacing: "-0.01em" }}>
                    {current.label}
                  </h3>
                </div>
                <p style={{ margin: "0 0 4px", fontSize: 15, color: "#fff", fontWeight: 600 }}>{current.one_liner}</p>
                <p style={{ margin: "0 0 22px", fontSize: 13, color: "#94A3B8", fontStyle: "italic" }}>
                  When to use it: {current.when_use}
                </p>

                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: current.color, textTransform: "uppercase", marginBottom: 10 }}>
                    Step-by-step
                  </div>
                  <ol style={{ margin: 0, paddingLeft: 22, lineHeight: 1.7 }}>
                    {current.walk_through.map((step, i) => (
                      <li key={i} style={{ marginBottom: 8, color: "#E6EEF8", fontSize: 14 }}>{step}</li>
                    ))}
                  </ol>
                </div>

                <div
                  style={{
                    background: "rgba(0,220,120,0.06)",
                    border: "1px solid rgba(0,220,120,0.25)",
                    borderRadius: 10,
                    padding: "14px 18px",
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, color: "#00DC78", textTransform: "uppercase", marginBottom: 8 }}>
                    🤖 What runs on autopilot here
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.65 }}>
                    {current.auto_runs.map((r, i) => (
                      <li key={i} style={{ marginBottom: 5, color: "#C6F3DC", fontSize: 13 }}>{r}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {section === "rhythm" && (
            <div data-testid="manual-rhythm">
              <h3 style={{ margin: "0 0 8px", color: "#fff", fontSize: 18 }}>Your Daily Rhythm</h3>
              <p style={{ margin: "0 0 20px", color: "#94A3B8", fontSize: 13.5, lineHeight: 1.6 }}>
                The system runs in the background 24/7. These are the moments where you and Shaneka step in to <em>conduct</em>, not <em>execute</em>. Stick to this rhythm for 14 days and the dashboard becomes second-nature.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {DAILY_RHYTHM.map((r, i) => (
                <div
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr)",
                      gap: 6,
                      padding: "12px 16px",
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 9,
                    }}
                  >
                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: r.who.startsWith("Shannon") ? "#00DC78" : r.who.startsWith("Shaneka") ? "#A78BFA" : "#F472B6" }}>
                        {r.who}
                      </span>
                      <span style={{ fontSize: 12, color: "#5AC8D8", fontWeight: 700 }}>{r.time}</span>
                    </div>
                    <span style={{ fontSize: 13.5, color: "#E6EEF8", lineHeight: 1.55 }}>{r.task}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === "faqs" && (
            <div data-testid="manual-faqs">
              <h3 style={{ margin: "0 0 16px", color: "#fff", fontSize: 18 }}>Quick Answers</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {FAQS.map((f, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "14px 18px",
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.06)",
                      borderRadius: 10,
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#5AC8D8", marginBottom: 6 }}>Q. {f.q}</div>
                    <div style={{ fontSize: 13.5, color: "#E6EEF8", lineHeight: 1.6 }}>{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "12px 28px",
            borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "rgba(255,255,255,0.02)",
            fontSize: 11.5,
            color: "#94A3B8",
          }}
        >
          <span>You can open this anytime — look for "📘 Operator Manual" on the Today hub.</span>
          <span style={{ color: "#5AC8D8", fontWeight: 700 }}>Built for Shannon &amp; Shaneka · Cushman-tier discipline</span>
        </div>
      </div>
    </div>
  );
};

export default OperatorManual;
